import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  IDistrictService,
  ICalculatedFieldConfigurationService,
  IApplicationContextService,
  IOpsViewerMessagesService,
  UserAuthorizationService,
} from '../../../services/services';
import {
  Features,
  CompanyWorkbookConfiguration,
  WellKnownMeasurementChoice,
  UnitSystemChoice,
  CompanyCalculatedFieldConfiguration,
  CompanyTypes
} from '../../../models/models';
import { ToastMessageOptions, SelectItem } from 'primeng/api';
import { Title } from '@angular/platform-browser';
import { firstValueFrom, Observable, of, combineLatest, BehaviorSubject } from 'rxjs';
import { map, switchMap, tap, shareReplay } from 'rxjs/operators';
import { Address, Company, CompanyClient, CompanyWorkbookConfigurationClient, CompanyWorkbookConfiguration as ApiCompanyWorkbookConfiguration, WorkbookMeasurementChannelConfiguration as ApiWorkbookMeasurementChannelConfiguration } from '../../../services/nswag/nswagclient';
import { IUserManagerService } from '../../../services/iUserManager.service';

type MessageSeverity = 'success' | 'info' | 'warn' | 'error' | 'secondary' | 'contrast';

@Component({
    // tslint:disable-next-line:component-selector
    selector: 'company-detail',
    templateUrl: './company-detail.component.html',
    styleUrls: ['./company-detail.component.css'],
    standalone: false
})

export class CompanyDetailComponent implements OnInit {
  // Async VM to drive the template via async pipe
  public vm$!: Observable<{
    serviceProviderOptions: SelectItem[];
    company: Company;
  }>;

  private companyId = '';
  public company: Company = Object.assign(new Company(), {
    billingAddress: new Address(),
    shippingAddress: new Address()
  });
  public messages: ToastMessageOptions[] = [];
  public isNew = false;
  public isAdmin = false;
  public categories: SelectItem[] = [];
  public districts: SelectItem[] = [];
  public addingDistrict = false;
  public newDistrictName = '';
  public billingAddressEmailValid = true;
  public shippingAddressEmailValid = true;
  public shippingAddressIsTheSame = true;
  public modelInvalid = false;
  public showModelInvalidDialog = false;
  public saving = false;

  public serviceProviderOptions: SelectItem[] = [];

  public workbookConfigurationUnitSystemChoices: SelectItem[] = [
    { label: UnitSystemChoice.si, value: UnitSystemChoice.si },
    { label: UnitSystemChoice.imperial, value: UnitSystemChoice.imperial }
  ];
  public workbookWellKnownMeasurements: WellKnownMeasurementChoice[] = [];
  public workbookWellKnownMeasurementOptions: SelectItem[] = [];
  public workbookActivities: SelectItem[] = [];
  // Move workbook configuration to an observable managed via BehaviorSubject
  private workbookConfigurationSubject = new BehaviorSubject<CompanyWorkbookConfiguration | null>(null);
  public workbookConfiguration$ = this.workbookConfigurationSubject.asObservable();

  public workbookConfigurationsDataTypes: SelectItem[] = [
    { label: 'Number', value: 'Number' },
    { label: 'Text', value: 'Text' }
  ];
  public workbookConfigurationCols: any[] = [];
  public workbookConfigurationDisplayDialog = false;
  public workbookConfigurationUnitOptions: SelectItem[] = [];

  public companyCalculatedFieldConfiguration?: CompanyCalculatedFieldConfiguration;

  public sandReportEmails = '';
  public autoApprovedEmailDomains = '';

  public companyTypes: SelectItem[] = [
    { label: CompanyTypes.Vendor, value: CompanyTypes.Vendor },
    { label: CompanyTypes.Consumer, value: CompanyTypes.Consumer }
  ];

  constructor(
    private _route: ActivatedRoute,
    private _router: Router,
    private _companyService: CompanyClient,
    private _districtService: IDistrictService,
    private _titleService: Title,
    private _workbookConfigurationClient: CompanyWorkbookConfigurationClient,
    private _calculatedFieldConfigurationService: ICalculatedFieldConfigurationService,
    private _applicationContextService: IApplicationContextService,
    private _messageService: IOpsViewerMessagesService,
    private _userManagerService: IUserManagerService,
    private _userAuthorizationService: UserAuthorizationService,) { }

  ngOnInit() {


    const serviceProviders$ = this._companyService.get().pipe(
      map(result => result.filter(r => r.companyType === CompanyTypes.Vendor)),
      map(serviceProviders => serviceProviders.map(r => ({ label: r.name, value: r.id }) as SelectItem)),
      tap(options => { this.serviceProviderOptions = options; }),
      shareReplay(1)
    );

    const company$ = this._route.params.pipe(
      map(params => params['id'] as string),
      tap(id => { this.companyId = id; }),
      switchMap(id => {
        if (!id || id.toUpperCase() === 'NEW') {
          this.isNew = true;
          const c = new Company();
          c.billingAddress = new Address();
          c.shippingAddress = new Address();
          // All new companies now enabled for offline workbook
          c.enableOfflineWorkbook = true;

          return of(c);
        }
        this.isNew = false;
        this.messages = [];
        return this._companyService.getById(id).pipe(
          tap(result => {
            if (result == null) {
              this.messages = [];
              this.messages.push({
                severity: 'error',
                summary: 'Could not find Company',
                detail: 'Company with Id (' + id + ') was not found'
              });
              return;
            }

            if (result.sandReportEmailAddresses) {
              this.sandReportEmails = result.sandReportEmailAddresses.join(';');
            }

            // prevent null address objects on page
            if (!result.billingAddress) { result.billingAddress = new Address(); }
            if (!result.shippingAddress) { result.shippingAddress = new Address(); }
            // Auto-check same address button if needed
            if (result.billingAddress !== result.shippingAddress) {
              this.shippingAddressIsTheSame = false;
            }

            // For old companies set companyType - TODO: Remove after Data Migration
            if (result.companyType === null) {
              result.companyType = CompanyTypes.Vendor;
            }

            if (result.autoApprovedEmailDomains) {
              this.autoApprovedEmailDomains = result.autoApprovedEmailDomains.join(';');
            }

            // Force-enable offline workbook for existing companies
            result.enableOfflineWorkbook = true;
          })
        );
      }),
      tap(c => {
        this.company = c;
        // Clear any previous workbook configuration on company change
        this.workbookConfigurationSubject.next(null);
        this.setPageTitle(this.companyId);
      }),
      shareReplay(1)
    );

    this.vm$ = combineLatest([serviceProviders$, company$]).pipe(
      map(([serviceProviderOptions, company]) => ({
        serviceProviderOptions,
        company
      }))
    );

    this.EnableRoleContent();
  }

  async EnableRoleContent(): Promise<void> {
    const user = await firstValueFrom(this._userManagerService.getCurrentUser(), { defaultValue: null });
    this.isAdmin = this._userAuthorizationService.isUserAnyRoleAdmin(user, true);
  }

  // Legacy imperative data-loading methods removed in favor of async streams

  setPageTitle(companyId: string) {
    if (!companyId || companyId === 'New' || companyId === '') {
      this._titleService.setTitle('New Job');
    } else { this._titleService.setTitle(companyId); }
  }
  generateApiKey() {
    this.company.apiKey = this.generateUUID().replace(/-/g, '');
  }
  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
  // getServiceProviders/getCompany/getFeatures replaced by streams in ngOnInit()

  getCategories() {
    this.categories = [];
  }

  getDistricts() {
    this._districtService.getDistricts().subscribe({
      next: results => {
        results.forEach(result => {
          this.districts.push({
            label: result.name,
            value: result.id
          });
        });
      },
      error: error => { this._messageService.errorMessage(error, 'Could not get Districts'); },
      complete: () => { /* complete */ }
    });
  }


  addDistrictDialog() {
    this.addingDistrict = true;
  }

  addDistrict() {
    this._districtService.createDistrict(this.newDistrictName).subscribe({
      next: _ => { /* no-op */ },
      error: error => { this._messageService.errorMessage(error, 'Could not create District'); },
      complete: () => {
        this.addingDistrict = false;
      }
    });
  }

  closeDistrictDialog() {
    this.addingDistrict = false;
  }

  handleErrors(event: any) {
    this.messages = event;
  }

  updateWorkbookConfiguration(event: CompanyWorkbookConfiguration) {
    this.workbookConfigurationSubject.next(event);
  }

  updateCompanyCalculations(event: any) {
    this.companyCalculatedFieldConfiguration = event;
  }

  updateSandReportEmails() {
    if (this.sandReportEmails) {
      this.company.sandReportEmailAddresses = this.sandReportEmails.split(';');
    }
  }

  updateEmailDomains() {
    if (this.autoApprovedEmailDomains) {
      this.company.autoApprovedEmailDomains = this.autoApprovedEmailDomains.split(';');
    }
  }



  validateCompanyModel(): boolean {
    if (!this.company.name) { return false; }
    if (!this.company.companyType) { return false; }
    // TODOD: Are any of the below required?  If so uncomment.
    //       Also uncomment validation messages in html
    // if(!this.company.category) return false;
    // if(!this.company.district) return false;
    // if(!this.validateBillingAddress()) return false;
    // if(!this.validateShippingAddress()) return false;
    // if(!this.validateEmails()) return false;

    return true;
  }

  validateBillingAddress() {
    if (!this.company.billingAddress?.name) { return false; }
    if (!this.company.billingAddress?.email) { return false; }
    if (!this.company.billingAddress?.phone) { return false; }
    if (!this.company.billingAddress?.street) { return false; }
    if (!this.company.billingAddress?.city) { return false; }
    if (!this.company.billingAddress?.state) { return false; }
    if (!this.company.billingAddress?.zip) { return false; }

    return true;
  }

  validateShippingAddress() {
    if (!this.company.shippingAddress?.name) { return false; }
    if (!this.company.shippingAddress?.email) { return false; }
    if (!this.company.shippingAddress?.phone) { return false; }
    if (!this.company.shippingAddress?.street) { return false; }
    if (!this.company.shippingAddress?.city) { return false; }
    if (!this.company.shippingAddress?.state) { return false; }
    if (!this.company.shippingAddress?.zip) { return false; }

    return true;
  }

  validateBillingEmail(event: any) {
    this.billingAddressEmailValid = event;
  }

  validateShippingEmail(event: any) {
    this.shippingAddressEmailValid = event;
  }

  validateEmails(): boolean {
    return this.billingAddressEmailValid && this.shippingAddressEmailValid;
  }

  saveCompany() {
    this.saving = true;
    if (this.shippingAddressIsTheSame && this.company) {
      this.company.shippingAddress = this.company.billingAddress;
    }

    this.showModelInvalidDialog = this.modelInvalid = !this.validateCompanyModel();

    if (!this.showModelInvalidDialog) {
      // Show global spinner while saving (align with job create/update UX)
      this._applicationContextService.showApplicationSpinner(false);
      if (this.shippingAddressIsTheSame) {
        this.company.shippingAddress = this.company.billingAddress;
      }

      if (this.isNew) {
        if (!this.company.unitSystem) {
          this.company.unitSystem = UnitSystemChoice.imperial;
        }
        this.company.id = this.generateUUID();
        this.companyId = this.company.id;
        this._companyService.post(this.company).subscribe({
          next: async () => {
            this._applicationContextService.alertCompaniesListUpdated();
            // Persist default workbook configuration for new companies when enabled
            if (this.company.enableOfflineWorkbook) {
              try {
                const unitSystem = this.company.unitSystem ?? UnitSystemChoice.imperial;
               
                const newWbConfig = await this.createDefaultCompanyWorkbookConfiguration(null, this.companyId, unitSystem);
                this.workbookConfigurationSubject.next(newWbConfig);
                await firstValueFrom(this.saveWorkboookConfiguration(this.companyId));
                // Hide spinner before navigating away
                this._applicationContextService.hideApplicationSpinner();
                this.saving = false;
                this.returnToCompanyList();
              } catch (e) {
                this._messageService.errorMessage(e, 'Could not create default workbook configuration');
                // Hide spinner even on failure and still navigate
                this._applicationContextService.hideApplicationSpinner();
                this.saving = false;
                // Even if WB config fails, still navigate to list
                this.returnToCompanyList();
                return;
              }
            } else {
              this._applicationContextService.hideApplicationSpinner();
              this.saving = false;
              this.returnToCompanyList();
              return;
            }
          },
          error: error => { 
            this._messageService.errorMessage(error, 'Could not Create Company');
            this._applicationContextService.hideApplicationSpinner();
            this.saving = false;
          }
        });
      } else {
        this._companyService.put(this.companyId, this.company).subscribe({
          next: async _ => {
            this._applicationContextService.alertCompaniesListUpdated();
            if (this.company.enableOfflineWorkbook) {
              await firstValueFrom(this.saveWorkboookConfiguration(this.companyId));
              this._applicationContextService.hideApplicationSpinner();
              this.saving = false;
              this.returnToCompanyList();
            } else {
              this._applicationContextService.hideApplicationSpinner();
              this.saving = false;
              this.returnToCompanyList();
            }
          },
          error: error => { 
            this._messageService.errorMessage(error, 'Could not Update Company');
            this._applicationContextService.hideApplicationSpinner();
            this.saving = false;
          }
        });
      }
      //this.saveCalculatedFieldConfiguration();
    } else {
      // Validation failed; ensure spinner not shown and reset saving state
      this._applicationContextService.hideApplicationSpinner();
      this.saving = false;
    }
  }

  async RecreateWorkbookConfiguration(): Promise<void> {
    if (!this.company.unitSystem) {
      console.error('Could not recreate Workbook configuration because the Unit System is no set.');
      return;
    }
    const current = this.workbookConfigurationSubject.getValue();
    const wbConfigId: string | null = current?.id ?? null;
    this.saving = true;
    const newWbConfig = await this.createDefaultCompanyWorkbookConfiguration(wbConfigId, this.companyId, this.company.unitSystem);
    this.workbookConfigurationSubject.next(newWbConfig);
    try {
      await firstValueFrom(this.saveWorkboookConfiguration(this.companyId));
    } finally {
      this.saving = false;
    }
  }

  async createDefaultCompanyWorkbookConfiguration(wbConfigurationId: string | null, companyId: string, unitSystem: string): Promise<CompanyWorkbookConfiguration> {
    const configuration = new CompanyWorkbookConfiguration(wbConfigurationId ?? '', companyId);
    const activityNames = await firstValueFrom(this._workbookConfigurationClient.getWorkbookActivityOptions());
    //const workbookActivities = activityNames.map(i => <SelectItem>{label: i, value: i});
    const apiWkms = await firstValueFrom(this._workbookConfigurationClient.getWellKnownMeasurements());
    // Map NSwag models to local WellKnownMeasurementChoice for setDefaultMeasurements
    const wkms = (apiWkms || []).map(r => ({
      name: r.name ?? '',
      displayName: r.displayName ?? (r.name ?? ''),
      siUnits: (r.siUnits || []).map(u => ({ unitKey: u.unitKey, unitName: u.unitName })),
      imperialUnits: (r.imperialUnits || []).map(u => ({ unitKey: u.unitKey, unitName: u.unitName })),
      defaultSiUnit: r.defaultSiUnit ? { unitKey: r.defaultSiUnit.unitKey, unitName: r.defaultSiUnit.unitName } : null,
      defaultImperialUnit: r.defaultImperialUnit ? { unitKey: r.defaultImperialUnit.unitKey, unitName: r.defaultImperialUnit.unitName } : null
    }));

    configuration.setDefaultMeasurements(activityNames, unitSystem, wkms);
    return configuration;
  }

  saveWorkboookConfiguration(companyId: string): Observable<void> {
    const current = this.workbookConfigurationSubject.getValue();
    if (!current) {
      this._messageService.errorMessage('Workbook configuration is empty', 'Nothing to save');
      // Return a completed observable since there's nothing to do
      return of(void 0);
    }
    // Map local CompanyWorkbookConfiguration to NSwag DTO
    const dto: ApiCompanyWorkbookConfiguration = new ApiCompanyWorkbookConfiguration();
    dto.id = current.id;
    dto.companyId = companyId;
    dto.workbookMeasurementChannelConfigurations = (current.workbookMeasurementChannelConfigurations || []).map(c => {
      const mc = new ApiWorkbookMeasurementChannelConfiguration();
      Object.assign(mc, c);
      return mc;
    });
    return this._workbookConfigurationClient.createOrUpdate(dto).pipe(
      tap({
        error: error => this._messageService.errorMessage(error, 'Could not Create or Update Company Workbookconfiguration')
      }),
      map(() => void 0)
    );
  }

  saveCalculatedFieldConfiguration() {
    if (this.companyCalculatedFieldConfiguration) {
      if (this.companyCalculatedFieldConfiguration.companyId === undefined) {
        this.companyCalculatedFieldConfiguration.companyId = this.companyId;
      }
      this._calculatedFieldConfigurationService.createOrUpdateConfiguration(this.companyCalculatedFieldConfiguration).subscribe({
        next: _ => {
          this.returnToCompanyList();
        },
        error: error => { this._messageService.errorMessage(error, 'Could not Create or Update Company Calculated Field Configuration'); }
      });
    }
  }

  closeInvalidModelDialog() {
    this.showModelInvalidDialog = false;
  }

  returnToCompanyList() {
    this._router.navigate(['/companies/']);
  }

  showApiHelp() {
    window.open('/api-help', '_blank');
  }

  asMessageSeverity(severity: ToastMessageOptions['severity'] | undefined): MessageSeverity | undefined {
    return severity as MessageSeverity | undefined;
  }

}
